import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter, distinctUntilChanged, takeUntil, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import * as AgentSelectors from '../../../store/agents.selectors';
import { AppState } from 'src/app/core/app.state';
import { IAgent } from '../../../models/agents.models';
import { ICatalog } from 'src/app/features/catalogs/models/catalogs.models';
import { environment } from 'src/environments/environment';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import * as agentsActions from '../../../store/agents.actions';
import { AgentCatalogsAddDialogComponent } from './agent-catalogs-add-dialog/agent-catalogs-add-dialog.component';
import { AgentProductPreferencesDialogComponent } from './agent-product-preferences-dialog/agent-product-preferences-dialog.component';
import { IBrand } from 'src/app/features/brands/models/brands.models';
import { ITag } from 'src/app/features/tags/models/tags.models';
import { ICategory } from 'src/app/features/categories/models/categories.models';
import { CatalogsService } from 'src/app/features/catalogs/services/catalogs.services';

@Component({
  selector: 'app-agent-catalogs',
  templateUrl: './agent-catalogs.component.html',
  styleUrls: ['./agent-catalogs.component.scss']
})
export class AgentCatalogsComponent implements OnInit, OnChanges {
  @Input() triggerAddCatalogs: boolean = false;

  agent$: Observable<IAgent | null> = this.store.select(
    AgentSelectors.selectAgent
  );
  agent: IAgent | null = null;
  catalogs: ICatalog[] = [];

  baseUrl = environment.BASE_URL_API;

  whitelistedPreferences: any[] = [];
  blacklistedPreferences: any[] = [];

  destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private router: Router,
    public dialog: MatDialog,
    private translateService: TranslateService,
    private catalogService: CatalogsService,
    private activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.agent$
      .pipe(
        filter((agent) => agent != null),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((agent: IAgent | null) => {
        this.agent = agent;
        if (agent && agent.pricelist_ids!) {
          this.catalogs = agent.pricelist_ids! as ICatalog[];
        }

        // Put all preferences in an array for the UI (whitelisted and blacklisted)
        // Collect whitelisted preferences (brands, tags, categories)
        this.whitelistedPreferences = [];
        if (agent) {
          // Brands
          if (agent.white_list_brands) {
            this.whitelistedPreferences.push(
              ...agent.white_list_brands.map((brand: any) => ({
                id: brand.id,
                name: brand.name,
                type: 'brand'
              }))
            );
          }
          // Tags (labels)
          if (agent.white_list_tags) {
            this.whitelistedPreferences.push(
              ...agent.white_list_tags.map((label: any) => ({
                id: label.id,
                name: label.name,
                type: 'label'
              }))
            );
          }
          // Categories
          if (agent.white_list_categories) {
            this.whitelistedPreferences.push(
              ...agent.white_list_categories.map((category: any) => ({
                id: category.id,
                name: category.name,
                type: 'category'
              }))
            );
          }
        }

        // Collect blacklisted preferences (brands, tags, categories)
        this.blacklistedPreferences = [];
        if (agent) {
          // Brands
          if (agent.black_list_brands) {
            this.blacklistedPreferences.push(
              ...agent.black_list_brands.map((brand: any) => ({
                id: brand.id,
                name: brand.name,
                type: 'brand'
              }))
            );
          }
          // Tags (labels)
          if (agent.black_list_tags) {
            this.blacklistedPreferences.push(
              ...agent.black_list_tags.map((label: any) => ({
                id: label.id,
                name: label.name,
                type: 'label'
              }))
            );
          }
          // Categories
          if (agent.black_list_categories) {
            this.blacklistedPreferences.push(
              ...agent.black_list_categories.map((category: any) => ({
                id: category.id,
                name: category.name,
                type: 'category'
              }))
            );
          }
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['triggerAddCatalogs'] && changes['triggerAddCatalogs'].currentValue) {
      this.openAddCatalogsDialog();
    }
  }

  goToNewCatalog(): void {
    // Clear old new catalog saved data
    this.catalogService.setNewCatalogData(null)
    this.router.navigate(['/agents/new-catalog'], { queryParams: { agentId: this.agent?.id } });
  }

  openAddCatalogsDialog() {
    this.dialog.open(AgentCatalogsAddDialogComponent, {
      data: {
        agent: this.agent,
        pricelist_ids: this.catalogs.map((catalog: ICatalog) => catalog.id!)
      },
    });
  }

  openProductPreferencesDialog(type: 'blacklist' | 'whitelist') {
    this.dialog.open(AgentProductPreferencesDialogComponent, {
      data: {
        agent: this.agent,
        type: type
      },
    });
  }

  // Delete a preference (brand, label, category)
  deletePreference(type: 'whitelist' | 'blacklist', preference: { id: number, type: 'brand' | 'label' | 'category' }) {
    if (!this.agent || !this.agent.id) return;

    // Prepare updated lists
    let updatedBrands = [];
    let updatedLabels = [];
    let updatedCategories = [];

    if (type === 'whitelist') {
      updatedBrands = (this.agent.white_list_brands as IBrand[] || []).filter((b: any) => !(preference.type === 'brand' && b.id === preference.id));
      updatedLabels = (this.agent.white_list_tags as ITag[] || []).filter((l: any) => !(preference.type === 'label' && l.id === preference.id));
      updatedCategories = (this.agent.white_list_categories as ICategory[] || []).filter((c: any) => !(preference.type === 'category' && c.id === preference.id));
    } else {
      updatedBrands = (this.agent.black_list_brands as IBrand[] || []).filter((b: any) => !(preference.type === 'brand' && b.id === preference.id));
      updatedLabels = (this.agent.black_list_tags as ITag[] || []).filter((l: any) => !(preference.type === 'label' && l.id === preference.id));
      updatedCategories = (this.agent.black_list_categories as ICategory[] || []).filter((c: any) => !(preference.type === 'category' && c.id === preference.id));
    }

    // Prepare update object
    const updateObj: any = {};
    if (type === 'whitelist') {
      updateObj.white_list_brands = updatedBrands.map((b: any) => b.id);
      updateObj.white_list_tags = updatedLabels.map((l: any) => l.id);
      updateObj.white_list_categories = updatedCategories.map((c: any) => c.id);
    } else {
      updateObj.black_list_brands = updatedBrands.map((b: any) => b.id);
      updateObj.black_list_tags = updatedLabels.map((l: any) => l.id);
      updateObj.black_list_categories = updatedCategories.map((c: any) => c.id);
    }

    this.store.dispatch(
      agentsActions.UpdateAgent({
        id: this.agent.id,
        agent: updateObj
      })
    );
  }

  deleteCatalog(catalogId: number): void {
    if (!this.agent$) return;

    this.agent$.pipe(take(1)).subscribe((agent) => {
      if (!agent || !agent.id) return;

      const agentCatalogs = agent.pricelist_ids! as ICatalog[];

      // Get all Catalog IDs except the ones to be deleted
      const updatedCatalogsIds = agentCatalogs!
        .map(catalog => catalog.id!)
        .filter(id => id !== catalogId);

      // Dispatch the update action with only the location_ids field
      this.store.dispatch(
        agentsActions.UpdateAgent({
          id: agent.id,
          agent: { pricelist_ids: updatedCatalogsIds as number[] },
        })
      );
    });
  }

  // Delete Confirmation Dialog for Catalogs and Preferences
  deleteConfirmation(deleteType: 'catalog' | 'preference', catalogId: number | null, preference?: { id: number, type: 'brand' | 'label' | 'category' }, type: 'whitelist' | 'blacklist' = 'whitelist') {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: 'assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove')
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        if (deleteType === 'catalog' && catalogId) {
          this.deleteCatalog(catalogId);
        }

        if (deleteType === 'preference' && preference) {
          this.deletePreference(type, preference);
        }
      }
    });
  }

  editCatalog(catalog: ICatalog) {
    this.router.navigate(['agents/edit-catalog', catalog.id], { queryParams: { agentId: this.agent?.id } })
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
